import { useEffect, useState, useMemo } from 'react'
import swal from 'sweetalert'

import Avatar from '@components/student-dashboard/gamification/avatar'

import { useTable, usePagination } from 'react-table'

import { CClient, getParsedTime } from '@common/utils'

import Spinner from '@components/common/spinner'
import { LeaderboardProps, LeaderboardItem } from '@lib/types/competitions'
import Link from 'next/link'
import AlertMessage from '@components/atoms/alert-message'
import { formatTimeByTimezone, TIME_FORMAT_TZ } from '@lib/utils/date.utils'

const LEADERBOARD_RESULTS_PAGE_SIZE = 50
const paginationButtonCount = 10
const paginationBreakPoint = 4

type Props = {
  eventSlug: string
  phaseSlug: string
  endTime?: string
  timezone?: string
}
const Leaderboard = ({ eventSlug, phaseSlug, endTime, timezone }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [leaderboard, setLeaderboard] = useState([])
  const [pageCount, setPageCount] = useState(0)
  const [initialPaginationIndex, setInitialPaginationIndex] = useState(0)

  const [
    currentUserDetails,
    setCurrentUserDetails,
  ] = useState<LeaderboardItem | null>(null)

  const formattedFreezeTime = useMemo(() => {
    if (!endTime || !timezone) return null
    const freezeTime = new Date(new Date(endTime).getTime() - 30 * 60 * 1000)
    return formatTimeByTimezone(freezeTime, timezone, TIME_FORMAT_TZ)
  }, [endTime, timezone])

  const fetchData = async (pageIndex: string) => {
    try {
      setIsLoading(true)
      const response = await CClient(
        `/api/v1/competitions/${eventSlug}/phases/${phaseSlug}/leaderboard/?page=${
          pageIndex + 1
        }&page_size=${LEADERBOARD_RESULTS_PAGE_SIZE}`
      )
      if (response.ok) {
        const data: {
          count: number
          results: LeaderboardProps
        } = await response.json()
        const parsedLeaderboardData = data.results.leaderboard
          // .filter((item) => item.rank !== data.results.currentUserDetails.rank)
          .map((item) => {
            const {
              rank,
              pointsObtained,
              cumulativeTime,
              student: { name, username, gender, avatar, grade },
            } = item

            return {
              rank,
              pointsObtained,
              cumulativeTime: getParsedTime(cumulativeTime),
              name,
              username,
              gender,
              avatar,
              grade,
              isCurrentUser: data.results.currentUserDetails
                ? rank === data.results.currentUserDetails.rank
                : false,
            }
          })

        setPageCount(Math.ceil(data.count / LEADERBOARD_RESULTS_PAGE_SIZE))
        setLeaderboard(parsedLeaderboardData)
        setCurrentUserDetails(data.results.currentUserDetails)
        setIsLoading(false)
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      console.error(error)
      setIsLoading(false)

      // Get detailed error message
      let errorMessage = 'There was an error fetching the leaderboard.'

      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`
      } else if (typeof error === 'string') {
        errorMessage += ` Error: ${error}`
      }

      swal({
        icon: 'error',
        title: 'Sorry!',
        text: `${errorMessage} Please try again later.`,
      })
    }
  }

  const paginationControl = () => {
    let finalIndex = 0
    if (pageIndex <= paginationBreakPoint) {
      finalIndex = 0
    } else if (pageIndex + 1 >= pageCount - paginationBreakPoint) {
      finalIndex =
        pageCount - paginationButtonCount < 0
          ? 0
          : pageCount - paginationButtonCount
    } else if (pageIndex > paginationBreakPoint) {
      finalIndex = pageIndex - paginationBreakPoint
    }

    setInitialPaginationIndex(finalIndex)
  }

  const columns = useMemo(
    () => [
      {
        Header: () => <div className="text-left ml-4">Name</div>,
        Cell: ({ row: { original } }) => {
          return (
            <div className="flex items-center">
              <Avatar
                image={original.avatar}
                gender={original.gender}
                className="w-8 h-8 rounded-full object-contain"
                name={original.name}
              />
              <div>
                <Link href={`/@${original.username}`}>
                  <a className="hover:underline ml-3 font-600">
                    {original.name}
                  </a>
                </Link>
                <div className="ml-3 text-grey-700">{original.grade}</div>
              </div>
            </div>
          )
        },
        accessor: 'name',
      },
      {
        Header: 'Rank',
        accessor: 'rank',
      },
      {
        Header: 'Total time',
        accessor: 'cumulativeTime',
      },
      {
        Header: 'Points',
        accessor: 'pointsObtained',
      },
      {
        Header: 'isCurrentUser',
        accessor: 'isCurrentUser',
      },
    ],
    []
  )

  const tableInstance = useTable(
    {
      columns,
      data: leaderboard,
      initialState: {
        pageIndex: 0,
        pageSize: LEADERBOARD_RESULTS_PAGE_SIZE,
        hiddenColumns: ['isCurrentUser'],
      },
      manualPagination: true,
      pageCount,
    },
    usePagination
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = tableInstance

  useEffect(() => {
    fetchData(pageIndex)
    paginationControl()
  }, [pageIndex])

  return (
    <div className="w-full flex flex-col items-center space-y-3 overflow-y-auto py-5">
      {formattedFreezeTime && (
        <AlertMessage type="info" className="max-w-2xl">
          <span className="text-grey text-base">
            Leaderboard will be frozen after {formattedFreezeTime} to maintain
            suspense. However, you can continue to make code submissions. Happy
            coding.
          </span>
        </AlertMessage>
      )}

      <div className="flex items-center justify-between max-w-6xl w-full md:relative">
        <p className="text-xl font-700 text-grey">Leaderboard</p>
      </div>
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner classes="h-10 w-10 text-orange" />
        </div>
      )}

      {!isLoading && (leaderboard.length > 0 || currentUserDetails) && (
        <>
          <div className="mx-auto w-full max-w-6xl text-sm border border-grey-300 rounded-lg">
            <table
              className="min-w-full divide-y divide-grey-300"
              {...getTableProps()}
            >
              <thead className="bg-grey-50">
                {
                  // Loop over the header rows
                  headerGroups.map((headerGroup, index) => (
                    // Apply the header row props
                    <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                      {
                        // Loop over the headers in each row
                        headerGroup.headers.map((column, index) => (
                          // Apply the header cell props
                          <th
                            key={index}
                            className="px-6 py-3 text-left text-tiny font-600 text-grey-900 uppercase tracking-wider"
                            {...column.getHeaderProps()}
                          >
                            {
                              // Render the header
                              column.render('Header')
                            }
                          </th>
                        ))
                      }
                    </tr>
                  ))
                }
              </thead>
              {/* Apply the table body props */}
              <tbody
                className="bg-white divide-y divide-grey-300 text-grey text-sm"
                {...getTableBodyProps()}
              >
                <>
                  {currentUserDetails &&
                    currentUserDetails.rank <
                      LEADERBOARD_RESULTS_PAGE_SIZE * pageIndex + 1 && (
                      <tr className="bg-orange-200">
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs flex items-center">
                          <Avatar
                            image={currentUserDetails.student.avatar}
                            gender={currentUserDetails.student.gender}
                            className="w-8 h-8 rounded-full object-contain"
                            name={currentUserDetails.student.name}
                          />
                          <div>
                            <Link
                              href={`/@${currentUserDetails.student.username}`}
                            >
                              <a className="hover:underline ml-3 font-600">
                                {currentUserDetails.student.name}
                              </a>
                            </Link>
                            <div className="ml-3 text-grey-700">
                              {currentUserDetails.student.grade}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs">
                          {currentUserDetails.rank}
                        </td>
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs">
                          {getParsedTime(currentUserDetails.cumulativeTime)}
                        </td>
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs">
                          {currentUserDetails.pointsObtained}
                        </td>
                      </tr>
                    )}
                  {
                    // Loop over the table rows
                    page.map((row, index) => {
                      // Prepare the row for display
                      prepareRow(row)
                      return (
                        // Apply the row props
                        <tr
                          key={index}
                          {...row.getRowProps()}
                          className={
                            row.values.isCurrentUser
                              ? 'bg-orange-200 text-grey'
                              : ''
                          }
                        >
                          {
                            // Loop over the rows cells
                            row.cells.map((cell, index) => {
                              // Apply the cell props
                              return (
                                <td
                                  key={index}
                                  className="px-6 py-3 whitespace-no-wrap max-w-xs"
                                  {...cell.getCellProps()}
                                >
                                  {
                                    // Render the cell contents
                                    cell.render('Cell')
                                  }
                                </td>
                              )
                            })
                          }
                        </tr>
                      )
                    })
                  }
                  {currentUserDetails &&
                    currentUserDetails.rank >
                      LEADERBOARD_RESULTS_PAGE_SIZE * (pageIndex + 1) && (
                      <tr className="bg-orange-200 ">
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs flex iterms-center">
                          <Avatar
                            image={currentUserDetails.student.avatar}
                            gender={currentUserDetails.student.gender}
                            className="w-8 h-8 rounded-full object-contain"
                            name={currentUserDetails.student.name}
                          />
                          <div>
                            <Link
                              href={`/@${currentUserDetails.student.username}`}
                            >
                              <a className="hover:underline ml-3 font-600">
                                {currentUserDetails.student.name}
                              </a>
                            </Link>
                            <div className="ml-3 text-grey-700">
                              {currentUserDetails.student.grade}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs">
                          {currentUserDetails.rank}
                        </td>
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs">
                          {getParsedTime(currentUserDetails.cumulativeTime)}
                        </td>
                        <td className="px-6 py-3 whitespace-no-wrap max-w-xs">
                          {currentUserDetails.pointsObtained}
                        </td>
                      </tr>
                    )}
                </>
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between pb-5">
            <div>
              {pageIndex !== 0 && (
                <>
                  <button
                    className={`py-2 px-4 mx-2 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                      canPreviousPage ? 'bg-white' : 'bg-grey-500'
                    }`}
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                  >
                    {'<<'}
                  </button>{' '}
                  <button
                    className={`py-2 px-4 mx-2 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                      canPreviousPage ? 'bg-white' : 'bg-grey-500'
                    }`}
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    {'<'}
                  </button>{' '}
                </>
              )}
              {pageCount > 0 &&
                [...Array(paginationButtonCount)].map(
                  (value: undefined, index) =>
                    initialPaginationIndex + index + 1 <= pageCount && (
                      <button
                        key={initialPaginationIndex + index + 1}
                        type="button"
                        onClick={() => gotoPage(initialPaginationIndex + index)}
                        className={`btn-orange shadow ${
                          pageIndex === initialPaginationIndex + index
                            ? 'bg-orange text-white'
                            : 'bg-white text-grey'
                        }  py-2 px-4 mx-2 rounded-md font-500 outline-none`}
                      >
                        {initialPaginationIndex + index + 1}
                      </button>
                    )
                )}

              {pageIndex !== pageCount - 1 && (
                <>
                  <button
                    className={`py-2 px-4 mx-2 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                      canNextPage ? 'bg-white' : 'bg-grey-500'
                    }`}
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    {'>'}
                  </button>{' '}
                  <button
                    className={`py-2 px-4 mx-2 rounded-md font-500 outline-none shadow hover:bg-orange-300 ${
                      canNextPage ? 'bg-white' : 'bg-grey-500'
                    }`}
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                  >
                    {'>>'}
                  </button>{' '}
                </>
              )}
            </div>
            <div className="w-32 font-500 text-grey text-right">
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageCount}
              </strong>{' '}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Leaderboard
